import React, { useEffect, useState } from "react";
import { Box, Checkbox, FormControl, Typography, Grid, Tooltip, Paper, Chip } from "@mui/material";
import { LockOutlined, VerifiedUserOutlined } from "@mui/icons-material";
import { observer } from "mobx-react";
import { useMasterTables } from "../../../stores/MasterTables";
import { useSaveUserInfo } from "../../../stores/User/user.mutations";
import FlatCard from "../../../atoms/FlatCard";

interface PermissionsAssignmentProps {
  userData: any;
  onSelectUser: (user: any) => void;
}

const getDirectPermissionIds = (userData: any): string[] =>
  (userData.permissions || [])
    .filter((p: any) => p.isUserPermission)
    .map((p: any) => p.id);

const PermissionAssignment: React.FC<PermissionsAssignmentProps> = observer(
  ({ userData, onSelectUser }) => {
    const { data: masterTablesData } = useMasterTables();
    const saveUserInfoMutation = useSaveUserInfo();

    // Permissions inherited from the user's role(s). These are always granted
    // and can only be removed by unassigning the role, so their checkboxes are
    // rendered checked + disabled.
    const rolePermissionIds: string[] = (userData.permissions || [])
      .filter((p: any) => !p.isUserPermission)
      .map((p: any) => p.id);

    // Directly assigned (user-specific) permissions, toggleable in this view.
    const [checkedDirectIds, setCheckedDirectIds] = useState<string[]>(() =>
      getDirectPermissionIds(userData)
    );

    // Re-sync when fresh user data arrives (initial fetch or after save), and
    // whenever the view switches to a different user. The id has to be in the
    // dependency list: keying on the `permissions` array alone means two users
    // whose arrays are both undefined (or share a reference) never trigger a
    // re-sync, and the next checkbox toggle would then PUT the previous user's
    // direct grants onto this one.
    useEffect(() => {
      setCheckedDirectIds(getDirectPermissionIds(userData));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id, userData?.permissions]);

    const permissionOptions: any[] = (masterTablesData || [])
      .filter((obj: any) => "permission" in obj)
      .map((obj: any) => obj.permission);

    const handleCheckboxChange = async (permissionId: string) => {
      if (rolePermissionIds.includes(permissionId)) return;

      const previousDirectIds = checkedDirectIds;
      const updatedDirectIds = checkedDirectIds.includes(permissionId)
        ? checkedDirectIds.filter((id) => id !== permissionId)
        : [...checkedDirectIds, permissionId];
      setCheckedDirectIds(updatedDirectIds);

      // Keep the role-inherited entries untouched and replace the direct
      // grants; the backend only persists entries with isUserPermission=true.
      const updatedPermissions = [
        ...(userData.permissions || []).filter((p: any) => !p.isUserPermission),
        ...updatedDirectIds.map((id) => ({ id, isUserPermission: true })),
      ];

      try {
        const response = await saveUserInfoMutation.mutateAsync({
          ...userData,
          permissions: updatedPermissions,
        });
        onSelectUser(response);
      } catch (error) {
        setCheckedDirectIds(previousDirectIds);
        console.error("API Error:", error);
      }
    };

    return (
      <Box>
        <FlatCard
          title={
            <Box display="flex" alignItems="center" gap={0.75}>
              <LockOutlined fontSize="small" sx={{ color: "secondary.main" }} />
              <span>Berechtigungen</span>
            </Box>
          }
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Direkte Berechtigungen aktivieren oder deaktivieren. Berechtigungen, die
            automatisch über eine Rolle vergeben werden, sind fixiert und können nur über die
            Rolle entfernt werden.
          </Typography>

          <Grid container spacing={1}>
            {permissionOptions[0]?.map((permission: any) => {
              const isFromRole = rolePermissionIds.includes(permission.id);
              const isChecked = isFromRole || checkedDirectIds.includes(permission.id);
              const checkbox = (
                <Checkbox
                  size="small"
                  checked={isChecked}
                  disabled={isFromRole || saveUserInfoMutation.isPending}
                  onChange={() => handleCheckboxChange(permission.id)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.25, mr: 0.75 }}
                />
              );
              return (
                <Grid key={permission.id} item xs={12} sm={6} md={4}>
                  <FormControl component="fieldset" fullWidth>
                    <Paper
                      elevation={0}
                      onClick={() =>
                        !isFromRole &&
                        !saveUserInfoMutation.isPending &&
                        handleCheckboxChange(permission.id)
                      }
                      sx={{
                        height: "100%",
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        cursor: isFromRole ? "default" : "pointer",
                        border: "1px solid",
                        borderColor: isChecked ? "secondary.main" : "divider",
                        backgroundColor: isChecked
                          ? "rgba(156, 26, 53, 0.04)"
                          : "background.paper",
                        transition: "all 0.15s ease-in-out",
                        "&:hover": {
                          borderColor: isFromRole ? "divider" : "secondary.main",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {isFromRole ? (
                          <Tooltip title="Wird automatisch über eine Rolle des Benutzers vergeben – kann hier nicht entfernt werden">
                            <span>{checkbox}</span>
                          </Tooltip>
                        ) : (
                          checkbox
                        )}
                        <Tooltip title={permission.description || ""} arrow>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            {permission.name}
                          </Typography>
                        </Tooltip>
                        {isFromRole && (
                          <Tooltip title="Wird automatisch über eine Rolle des Benutzers vergeben" arrow>
                            <Chip
                              icon={<VerifiedUserOutlined sx={{ fontSize: 14 }} />}
                              label="Rolle"
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.65rem", ml: 0.5 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Paper>
                  </FormControl>
                </Grid>
              );
            })}
          </Grid>
        </FlatCard>
      </Box>
    );
  }
);

export default PermissionAssignment;
